import { useState } from "react";
import { useProjectTags } from "../hooks/useProjectTags";
import { useAddTags, useRemoveTags, useTags } from "../hooks/usePortfolioTags";

interface Props {
  projectId: string;
}

export function ProjectTagEditor({ projectId }: Props) {
  const { data: tags, isLoading } = useProjectTags(projectId);
  const { data: allTags } = useTags();
  const addMutation = useAddTags();
  const removeMutation = useRemoveTags();

  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const handleAdd = () => {
    if (!newKey.trim() || !newValue.trim()) return;
    addMutation.mutate(
      { projectIds: [projectId], tags: [{ key: newKey.trim(), value: newValue.trim() }] },
      {
        onSuccess: () => {
          setNewKey("");
          setNewValue("");
          setAdding(false);
        },
      },
    );
  };

  const handleRemove = (key: string, value: string) => {
    removeMutation.mutate({
      projectIds: [projectId],
      tags: [{ key, value }],
    });
  };

  const entries = tags ? Object.entries(tags) : [];
  const hasAnyTags = entries.some(([, values]) => values.length > 0);

  return (
    <div className="rounded-xl bg-surface-700 border border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-body-sm font-semibold text-text-primary flex items-center gap-2">
          <TagIcon />
          Tags
        </h3>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="text-body-sm text-quantum-400 hover:text-quantum-300 transition-colors"
          >
            + Add
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="h-8 rounded bg-surface-600 animate-pulse" />
      ) : (
        <>
          {hasAnyTags ? (
            <div className="flex flex-wrap gap-2">
              {entries.map(([key, values]) =>
                values.map((value) => (
                  <span
                    key={`${key}:${value}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-surface-600 text-body-sm"
                  >
                    <span className="text-text-muted">{key}:</span>
                    <span className="text-text-secondary">{value}</span>
                    <button
                      onClick={() => handleRemove(key, value)}
                      disabled={removeMutation.isPending}
                      className="ml-0.5 text-text-muted hover:text-health-400 transition-colors"
                    >
                      <XIcon />
                    </button>
                  </span>
                )),
              )}
            </div>
          ) : !adding ? (
            <p className="text-body-sm text-text-muted">No tags assigned</p>
          ) : null}

          {adding && (
            <div className="mt-2 flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[120px]">
                <label className="block text-caption text-text-muted mb-1">Key</label>
                <input
                  type="text"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  className="w-full rounded-lg bg-surface-600 border border-border px-2.5 py-1.5 text-body-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
                  placeholder="e.g. division"
                  list="project-tag-keys"
                  autoFocus
                />
                {allTags && allTags.length > 0 && (
                  <datalist id="project-tag-keys">
                    {allTags.map((t) => (
                      <option key={t.key} value={t.key} />
                    ))}
                  </datalist>
                )}
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-caption text-text-muted mb-1">Value</label>
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="w-full rounded-lg bg-surface-600 border border-border px-2.5 py-1.5 text-body-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-quantum-500"
                  placeholder="e.g. AWS"
                  list="project-tag-values"
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
                {allTags && (
                  <datalist id="project-tag-values">
                    {allTags
                      .filter((t) => t.key === newKey)
                      .flatMap((t) => t.values)
                      .map((v) => (
                        <option key={v} value={v} />
                      ))}
                  </datalist>
                )}
              </div>
              <button
                onClick={handleAdd}
                disabled={!newKey.trim() || !newValue.trim() || addMutation.isPending}
                className="px-3 py-1.5 rounded-lg text-body-sm bg-quantum-500 text-white hover:bg-quantum-600 transition-colors disabled:opacity-50"
              >
                {addMutation.isPending ? "..." : "Add"}
              </button>
              <button
                onClick={() => { setAdding(false); setNewKey(""); setNewValue(""); }}
                className="px-3 py-1.5 rounded-lg text-body-sm text-text-muted hover:bg-surface-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TagIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
